import React from "react";
import { Link } from "react-router-dom";

import { buildAuditUrl } from "./auditEntityConfig";
import {
  formatAuditContext,
  formatAuditDate,
  getAuditDisplayAction,
  getAuditRecordLabel,
} from "./auditFormatters";
import AuditActionBadge from "./AuditActionBadge";
import AuditChangesTable from "./AuditChangesTable";
import AuditRelationsSection from "./AuditRelationsSection";
import AuditSnapshotSection from "./AuditSnapshotSection";
import PricingAuditChanges from "./PricingAuditChanges";

function AuditContextNote({ context }) {
  const text = formatAuditContext(context);
  if (!text) return null;
  return <p className="mt-2 text-sm text-[#64748B]">{text}</p>;
}

function RecordRef({ event, entitySlug, showRecordRef }) {
  if (!showRecordRef || !event.entity_id || !entitySlug) return null;

  const label = getAuditRecordLabel(event);
  const displayName = label || `#${event.entity_id}`;

  return (
    <p className="mt-2 text-sm text-[#64748B]">
      Record:{" "}
      <Link
        to={buildAuditUrl(entitySlug, event.entity_id, label ?? "")}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-[#2563EB] hover:text-[#1D4ED8] hover:underline"
      >
        {displayName}
      </Link>
    </p>
  );
}

export default function AuditEventCard({
  event,
  showRecordRef,
  entitySlug,
  onRestoreRequest,
  restoringAuditId,
}) {
  const { metadata, created_at, user_id, user } = event;
  const action = metadata?.action;
  const displayAction = getAuditDisplayAction(metadata);
  const canRestore =
    event.restore_available === true &&
    (action === "deleted" || action === "updated");
  const isRestoring = restoringAuditId === event.id;
  const showBlockedReason =
    !canRestore &&
    (action === "deleted" || action === "updated") &&
    event.restore_reason;

  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <AuditActionBadge action={displayAction} />
          {user_id ? (
            <span className="text-sm text-[#64748B]">
              {user ? `User: ${user.name}` : `User #${user_id}`}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {canRestore && onRestoreRequest ? (
            <button
              type="button"
              onClick={() => onRestoreRequest(event)}
              disabled={isRestoring}
              title={event.restore_reason || undefined}
              className="inline-flex items-center rounded-lg border border-[#2563EB] bg-white px-2.5 py-1 text-xs font-semibold text-[#2563EB] shadow-sm transition hover:bg-[#EFF6FF] disabled:opacity-50"
            >
              {isRestoring ? "Restoring…" : "Restore"}
            </button>
          ) : null}
          {showBlockedReason ? (
            <span
              className="max-w-xs truncate text-xs text-[#94A3B8]"
              title={event.restore_reason}
            >
              {event.restore_reason}
            </span>
          ) : null}
          <time className="text-xs text-[#94A3B8]">
            {formatAuditDate(created_at)}
          </time>
        </div>
      </div>

      <RecordRef
        event={event}
        entitySlug={entitySlug}
        showRecordRef={showRecordRef}
      />

      {action === "restored" && metadata?.source_audit_id ? (
        <p className="mt-2 text-sm text-[#64748B]">
          Restored from audit #{metadata.source_audit_id}
        </p>
      ) : null}

      {!event.pricing_presentation ? <AuditContextNote context={metadata?.context} /> : null}

      {action === "created" || action === "deleted" ? (
        <AuditSnapshotSection snapshot={metadata?.snapshot} />
      ) : null}

      {action === "updated" ? (
        <>
          {event.pricing_presentation ? (
            <PricingAuditChanges presentation={event.pricing_presentation} />
          ) : (
            <AuditChangesTable changes={metadata?.changes} />
          )}
          <AuditRelationsSection relations={metadata?.relations} />
        </>
      ) : null}
    </div>
  );
}
